// ── Tile-level types ────────────────────────────────────────────────
export type SurfaceType = 'grass' | 'field' | 'stone' | 'road' | 'path';
export type SeedType = 'wheat' | 'corn' | 'carrot';
export const SEED_TYPES: SeedType[] = ['wheat', 'corn', 'carrot'];

export type ToolId =
  | 'hands'
  | 'field'
  | 'plant'
  | 'seedBag'
  | 'scythe'
  | 'axe'
  | 'pickaxe'
  | 'water';

export interface Crop {
  type: SeedType;
  stage: number;     // 0..MAX_STAGE
  timer: number;     // seconds until next stage
}

/** Wild-land props the player chops/clears before farming. */
export type Thing = 'tree-oak' | 'tree-oak-sapling' | 'tree-oak-stump' | 'bush' | 'bush-sapling' | 'rock-boulder' | 'tall-grass' | null;

/** Player-placed structures (single-tile footprints for prototype). */
export type Built =
  | 'fence-post' | 'fence-rail-h' | 'fence-rail-v' | 'sign' | 'scarecrow' | null;

/** Buildings placed by the player on a cleared field tile. */
export type BuildingType = 'granary' | 'mill' | 'mailbox' | 'truck-depot';

/** Cosmetic decor — flowers, small rocks. */
export type Decor = 'flower-daisy' | 'flower-poppy' | 'rock-small' | null;

export interface ConstructionState {
  /** Number of stages built so far (1..totalStages). When this equals
   *  `totalStages` the building is operational and `construction` is cleared. */
  stage: number;
  totalStages: number;
  /** Materials the player has already deposited toward the NEXT stage,
   *  but which haven't yet been enough to pay for it. Reset to zeros
   *  whenever a stage advances. Players can stage materials in the
   *  building's crate as they collect them — once stagePaid covers the
   *  next-stage cost, the stage advances automatically. */
  stagePaid?: { money: number; wood: number; wheat: number; stone: number };
}

export interface Tile {
  surf: SurfaceType;
  crop: Crop | null;
  /** Building type on this tile (set on every footprint tile, not just anchor). */
  bld: BuildingType | null;
  /**
   * Multi-tile footprint pointer — for buildings >1×1, every tile in the
   * footprint stores the same `(c, r)` of the anchor (SW corner). When
   * undefined the tile itself is the anchor (1×1 buildings, legacy saves).
   */
  bldAnchor?: { c: number; r: number };
  /** Building under construction — when set the building is not yet
   *  operational. Lives on the anchor tile only. */
  construction?: ConstructionState;
  /** Mill-only: seconds remaining in current batch (0 = idle). Lives on anchor tile. */
  millRemaining?: number;
  /** Mill-only: wheat consumed for the active batch. */
  millInputUsed?: number;
  /** Mill-only: queue of pending batches. The HEAD entry (index 0) is the
   *  currently-grinding batch — its `qty` is decremented by 1 each time
   *  `millRemaining` hits 0 (a flour unit is added per tick). When `qty`
   *  reaches 0 the entry is shifted off and the next one starts. */
  millQueue?: { kind: 'wheat' | 'corn'; qty: number }[];

  thing?: Thing;
  built?: Built;
  decor?: Decor;
  watered?: boolean;
  /** Seconds left before an empty tilled tile dries back to grass.
   *  Set by CropSystem.placeField; cleared by plant; refreshed by water;
   *  ticked down by CropSystem.tick. */
  surfTimer?: number;
  /** Mushroom growth on a stump: 0 = none, 1 = growing, 2 = mature.
   *  Only meaningful while `thing === 'tree-oak-stump'`. Resets on harvest. */
  mushStage?: number;
  /** Seconds until the next mushroom stage tick. */
  mushTimer?: number;
  /** Berry ripeness on a bush: 0 = bare, 1 = green/growing, 2 = ripe. */
  berryStage?: number;
  /** Seconds until the next berry stage tick. */
  berryTimer?: number;
  /** Seconds left before a 'bush-sapling' matures into a full bush. */
  saplingTimer?: number;
  /** Seconds left before a 'tree-oak-sapling' matures into a full oak. */
  treeSaplingTimer?: number;
  /** A felled trunk lying on this tile (only set while `thing === 'tree-oak-stump'`).
   *  Hosts a faster, higher-yield mushroom colony than the stump alone. Player
   *  can chop it again to harvest the lumber, OR leave it for the fungi. */
  fallen?: boolean;
}

/** One adopted farm session — a set of tiles and the per-sub-cell work
 *  the player has done on them. Multiple PlotSessions coexist; the array
 *  lives on SimState.plots[]. */
export interface PlotSession {
  /** Player-given label — shown in the plot picker / HUD. Optional. */
  name?: string;
  tiles: { c: number; r: number }[];
  tilled: { c: number; r: number; subC: number; subR: number; stage: number }[];
  seeds: { c: number; r: number; subC: number; subR: number;
           type: SeedType; stage: number; timer: number;
           ox: number; oz: number; growMul: number }[];
  wet:   { c: number; r: number; subC: number; subR: number; stage: number; age?: number }[];
}

// ── Plot (the single big lot) ───────────────────────────────────────
export interface Plot {
  width: number;
  height: number;
  tiles: Tile[][];   // tiles[row][col]
}

// ── Truck / mailbox / city economy ──────────────────────────────────
/** Items that can be loaded into the truck and sold in the city. */
export type CargoKind =
  | 'wheat' | 'corn' | 'carrot' | 'flour' | 'cornFlour' | 'wood'
  | 'mushroom' | 'berry' | 'acorn';
export const CARGO_KINDS: CargoKind[] =
  ['wheat', 'corn', 'carrot', 'flour', 'cornFlour', 'wood', 'mushroom', 'berry', 'acorn'];
export type CargoCounts = Record<CargoKind, number>;
/** Fresh zeroed cargo record for inits + resets. */
export function emptyCargo(): CargoCounts {
  return {
    wheat: 0, corn: 0, carrot: 0, flour: 0, cornFlour: 0, wood: 0,
    mushroom: 0, berry: 0, acorn: 0,
  };
}

export interface TruckState {
  /** 'idle' = parked; 'loading' = at the depot accepting cargo;
   *  'departed' = en route to city; 'returning' = coming back with coins */
  status: 'idle' | 'loading' | 'departed' | 'returning';
  /** Loaded cargo (auto-loaded from platform on arrival; also accepts
   *  manual loads during the load window). */
  cargo: CargoCounts;
  /** Items the player has pre-loaded onto the depot platform. The truck
   *  picks these up automatically on arrival, so the player doesn't have
   *  to be present. */
  platform?: CargoCounts;
  /** Seconds until status auto-advances (loading→departed→returning→idle). */
  timer: number;
  /** Earnings on the current return trip (set when departing). */
  returnPayload: number;
  /** Items sold on the current return trip (set when departing). Used so
   *  the `sold` event can report a non-zero item count after cargo has
   *  already been cleared at dispatch time. */
  returnItems?: number;
  /** Pending seed purchases — the player buys seeds via the truck panel,
   *  the truck takes them to town on its next departure, and they're
   *  delivered on return. Each entry is one queued order; multiple are
   *  carried as a list. Cleared on truck return. */
  pendingSeeds?: SeedOrderLine[];
  /** Pending stone order placed via the truck panel. Player pays up-front,
   *  truck delivers the stone to inventory on return. Cleared on return. */
  pendingStone?: { qty: number; cost: number };
  /** Countdown to next scheduled arrival when idle (seconds). */
  nextArrival: number;
}

export interface SeedOrderLine {
  seed: SeedType;
  qty: number;
}
export interface MailboxState {
  /** A pending order being delivered. Null = nothing inbound. */
  pending: SeedOrderLine[] | null;
  /** Seconds until the order arrives. */
  eta: number;
  /** Coins paid for the pending order (committed). */
  paid: number;
}

// ── Player state ────────────────────────────────────────────────────
export interface ProgressState {
  xp: number;
  level: number;
  unlocked: Record<string, boolean>;
}

export interface ResourceState {
  money: number;
  energy: number;
  maxEnergy: number;
  basket: number;            // wheat in storage
  corn: number;
  carrot: number;
  flour: number;             // wheat flour (legacy name kept for back-compat)
  /** Corn flour — produced by the mill when grinding corn batches. */
  cornFlour?: number;
  wood: number;
  /** Stone collected by breaking boulders with the pickaxe. Used as a
   *  building material in stage-2+ buildings. */
  stone?: number;
  /** Acorns harvested from chopped trees — plantable to grow new oaks. */
  treeSeeds?: number;
  /** Mushrooms harvested off stumps. Sellable / future-food crop. */
  mushrooms?: number;
  /** Berries harvested off bushes. Also act as the bush "seed" — replanting
   *  a berry on a cleared tile grows a sapling → bush. */
  berries?: number;
  /** Seeds owned (consumed when planting). */
  seeds: Record<SeedType, number>;
}

export interface SimState {
  version: number;
  plot: Plot;
  res: ResourceState;
  prog: ProgressState;
  selectedSeed: SeedType;
  selectedBuilding: BuildingType | null;
  tool: ToolId;
  /** Placement mode: when set, next tile click places the chosen building. */
  placing: { type: BuildingType } | null;
  truck: TruckState;
  mailbox: MailboxState;
  time: number;
  /** Transient close-up zoom on a 6×6 sector. Reset on load. */
  zoom?: { active: boolean; c: number; r: number };
  /** Where each moveable world prop lives, in tile coords. The player can
   *  reposition them via a panel button. Persisted across saves. */
  props?: PropLayout;
  /** Transient: when set, the next tile click relocates the named prop. */
  moving?: { kind: PropKind } | null;
  /** Transient: drag-select mode for adopting a new farming plot.
   *  Tiles are accumulated as the player drags; release prompts confirm. */
  selecting?: { tiles: { c: number; r: number }[] } | null;
  /** Transient: the next tile click plants a berry on a cleared grass tile.
   *  Kept for backwards-compat; new flow is via the plot seed pile (the
   *  `selectedSpecialSeed` route below). */
  placingBerry?: boolean;
  /** Transient: when the plant tool is equipped inside an adopted plot, this
   *  routes the click to plant a berry / acorn at tile-level instead of
   *  drag-painting crop seeds at sub-cell level. Cleared when the player
   *  picks any regular crop seed (wheat/corn/carrot). */
  selectedSpecialSeed?: 'berry' | 'acorn' | null;
  /** Transient: edit-mode toggle for the active plot. While true, clicking
   *  a plot tile removes it from the active plot's tile list. */
  editingPlot?: boolean;
  /** Transient: the Zones manager is open. Plot button toggles this; while
   *  open the sidebar lists existing plots and tile clicks route through
   *  the manager (create / add / remove) rather than the world tool. */
  zonesMode?: boolean;
  /** Transient: which existing plot the zones manager is currently editing
   *  tile membership for (clicks add or remove tiles). null = browsing. */
  zonesEditPlotIdx?: number | null;
  /** Every plot the player has ever adopted. Each one PERSISTS — work
   *  done on it (tilling, seeding, watering) survives view exits, save/
   *  load, and adoption of new plots elsewhere. The player can have many
   *  plots scattered across the world. */
  plots: PlotSession[];
  /** Index into plots[] for the currently-active plot, OR null when in
   *  overview / no plot adopted. Transient — always derived from camera
   *  state at runtime. */
  activePlotIdx?: number | null;
  /** World-wide weeds — wild weed tufts on every grass tile in the world.
   *  Generated at world creation; player can pluck them anywhere (in plot
   *  view OR overview). Plucking persists. */
  weeds: { c: number; r: number; subC: number; subR: number }[];
  /** Free-tile sub-cell work — sub-cell tilling/seeding/watering on tiles
   *  that DON'T belong to any adopted plot. Same shape as PlotSession's
   *  arrays. When the player adopts a plot, overlapping entries migrate
   *  into the plot's arrays; when a plot is edit-removed back to free,
   *  entries return here. This is what lets the player farm anywhere in
   *  overview without having to adopt a plot first. */
  freeTilled: { c: number; r: number; subC: number; subR: number; stage: number }[];
  freeSeeds:  { c: number; r: number; subC: number; subR: number;
                type: SeedType; stage: number; timer: number;
                ox: number; oz: number; growMul: number }[];
  freeWet:    { c: number; r: number; subC: number; subR: number; stage: number; age?: number }[];
  /** TRANSIENT: true while the camera is diving on the plot. Independent
   *  of activePlot — the plot's data persists across view exits. */
  plotInView?: boolean;
}

export type PropKind = 'storage' | 'seedbag' | 'toolrack';
export interface PropLayout {
  storage:  { c: number; r: number };
  seedbag:  { c: number; r: number };
  toolrack: { c: number; r: number };
}

// ── Constants ───────────────────────────────────────────────────────
export const PLOT_W = 30;
export const PLOT_H = 30;
/** Rows >= ROAD_ROW are the road strip at the south edge of the lot.
 *  6 rows of road give the truck a visible drive distance south of the
 *  depot before it disappears off the south edge of the world. */
export const ROAD_ROW = PLOT_H - 6;

export const CROP_MAX_STAGE = 4;
export const CROP_GROW_TIMES = [3, 4, 4, 5];  // legacy default
export const CROP_GROW_TIMES_BY_TYPE: Record<SeedType, number[]> = {
  wheat:  [3, 4, 4, 5],
  corn:   [5, 6, 6, 7],
  carrot: [4, 5, 5, 6],
};

export const ENERGY_COST = {
  placeField: 5,
  plant: 3,
  harvest: 2,
  loadTruck: 1,
  buyBuilding: 6,
  startMill: 4,
  chopTree: 4,
  clearStump: 2,
  clearBush: 2,
  water: 1,
  forage: 1,
  plantBerry: 2,
};

export const ENERGY_REGEN = 1.2;

export const PRICES: Record<CargoKind, number> = {
  wheat: 3,
  corn: 6,
  carrot: 4,
  flour: 8,
  cornFlour: 10,
  wood: 5,
  mushroom: 4,
  berry: 3,
  acorn: 2,
};

/** Mushroom growth — fungus pops on a stump and matures in two stages.
 *  After harvest the timer rolls back to stage 0 so the stump can regrow. */
export const MUSHROOM_GROW_TIME = 35;        // seconds per stage (0→1, 1→2)
export const MUSHROOM_REGROW_DELAY = 18;     // seconds AFTER harvest before stage 0 starts again

/** Berry growth on a standing bush. */
export const BERRY_GROW_TIME = 28;           // seconds per stage
export const BERRY_REGROW_DELAY = 14;        // seconds after harvest before next cycle

/** Bush sapling → bush maturation. */
export const SAPLING_GROW_TIME = 60;
/** Tree sapling → full oak maturation. */
export const TREE_SAPLING_GROW_TIME = 120;

export const SEED_COSTS: Record<SeedType, number> = {
  wheat: 2,
  corn: 4,
  carrot: 3,
};
/** Legacy single-cost — used as a fallback in older paths. */
export const SEED_COST = 2;

/** Per-stone price when ordering through the truck. Picked to sit between
 *  wood (5c/sell) and flour (8c/sell) — stone is a building material, not
 *  a crop, so the import-buy cost reflects scarcity, not market resale. */
export const STONE_BUY_PRICE = 8;

export interface StageCost {
  money?: number;
  wood?: number;
  wheat?: number;
  stone?: number;
}

/**
 * Construction stages per building. Bigger / more important buildings have
 * more stages and a richer mix of materials. The sum of all stages is the
 * total cost to fully build.
 */
export const BUILDING_STAGES: Record<BuildingType, StageCost[]> = {
  granary: [
    { money: 30, wood: 4, stone: 6 },          // 1. stone foundation
    { money: 50, wood: 4, wheat: 5 },          // 2. plank walls + thatched roof
  ],
  mill: [
    { money: 50, wood: 6, stone: 10 },         // 1. stone foundation
    { money: 60, wood: 4, wheat: 4 },          // 2. tower walls + storage
    { money: 50, wood: 4, wheat: 4 },          // 3. cap + sails (test grain)
  ],
  mailbox:       [],                         // fixture — never under construction
  'truck-depot': [],
};

/** Sum of every stage cost — used by the Build panel for "total commitment". */
export function totalBuildingCost(type: BuildingType): Required<StageCost> {
  const sum: Required<StageCost> = { money: 0, wood: 0, wheat: 0, stone: 0 };
  for (const s of BUILDING_STAGES[type]) {
    sum.money += s.money ?? 0;
    sum.wood  += s.wood  ?? 0;
    sum.wheat += s.wheat ?? 0;
    sum.stone += s.stone ?? 0;
  }
  return sum;
}

/** Legacy single-step cost — equal to the first stage's cost for player-built
 *  buildings, kept around for any older code path that still reads it. */
export const BUILDING_COST: Record<BuildingType, { money: number; wood: number }> = {
  granary:      { money: 30, wood: 4 },
  mill:         { money: 50, wood: 6 },
  mailbox:      { money: 0,  wood: 0 },
  'truck-depot':{ money: 0,  wood: 0 },
};

/** Footprint of each building in tiles. Anchor is SW corner. */
export const BUILDING_SIZE: Record<BuildingType, { w: number; d: number }> = {
  granary:       { w: 2, d: 2 },
  mill:          { w: 2, d: 2 },
  mailbox:       { w: 1, d: 1 },
  'truck-depot': { w: 1, d: 1 },
};

export const BASKET_CAP = 200;
/** Seconds an empty tilled tile stays usable before it dries back to grass.
 *  Refreshed each time you water, cleared the moment you plant. */
export const FIELD_DRY_TIME = 90;
/** Seconds for fully-soaked water to evaporate one stage. Stage 3 → 2 → 1
 *  → 0 over 3× this duration. With DAY_LENGTH=180, 120s/stage = ~2 days
 *  total for a fully-soaked sub-cell to dry out. */
export const WATER_EVAPORATE_TIME = 120;

// ── Plot mode sub-cell grid ─────────────────────────────────────────
/** Sub-cells per plot tile, per axis. 8×8 = 64 sub-cells/tile gives
 *  enough resolution to scatter wheats off-grid (combined with per-seed
 *  `ox`/`oz` offsets in [-0.5, 0.5]) without overwhelming the player's
 *  planting throughput. Was bumped to 16×16 = 256 briefly; reverted at
 *  user request (the higher density made tilling tedious). v10 re-bumps
 *  to 16 because at 8×8 the hoed-furrow mesh (sized at 1/16 in Diorama)
 *  no longer tiled correctly and the tilled ground rendered as a sparse
 *  cube grid with green gaps. 16 restores the dense voxel furrow. */
export const PLOT_SUB_DIM = 16;

// ── Plot mode seed thresholds ───────────────────────────────────────
/** Minimum seeds sown on a plot tile before any will germinate.
 *  Encourages the player to commit to a crop rather than scatter one or
 *  two seeds. Below this threshold, seeds stay buried at stage 0. */
export const SEED_MIN_PER_TILE = 8;
/** Hard cap on seeds per plot tile (out of 64 sub-cells — leaves room
 *  for paths, gaps, and over-crowding penalty). */
/** Max seeds on a single plot tile. With 16×16=256 sub-cells, capping at
 *  64 maps to a stride-2 grid (every other sub-cell), giving a clean
 *  evenly-spaced 8×8 planting pattern instead of a dense thicket. */
export const SEED_MAX_PER_TILE = 64;
/** Seconds an under-seeded sub-cell (stage 0, tile count < SEED_MIN_PER_TILE)
 *  waits before withering and freeing the sub-cell. Without this, isolated
 *  seeds sit forever and pile up as a hidden inventory drain. */
export const SEED_DUD_TIMEOUT = 90;
/** Per-seed growth-rate multiplier range. Each seed picks a random value
 *  in [1 - GROW_MUL_JITTER, 1 + GROW_MUL_JITTER] on placement so
 *  neighbouring plants visibly ripen at different times. */
export const GROW_MUL_JITTER = 0.40;

// ── Calendar ────────────────────────────────────────────────────────
/** Seconds of real time per in-game day. 180 = 3 minutes per day.
 *  A full real-world year (365 days) therefore costs ~18.25 hours of
 *  play time. That's intentional — the calendar is meant to be a
 *  long-arc structure that future seasonal-farming mechanics hook into
 *  (which months a crop tolerates, when winter kills outdoor growth,
 *  greenhouse override, etc). Most players will never see a Year 2;
 *  the value is in WHERE in the year they are, not how fast it loops. */
export const DAY_LENGTH = 180;
/** Game starts mid-morning so the first impression is bright daylight. */
export const TIME_INITIAL_OFFSET = 0.35 * DAY_LENGTH;

export type Season = 'spring' | 'summer' | 'fall' | 'winter';
export const SEASONS: Season[] = ['spring', 'summer', 'fall', 'winter'];

export type Month =
  | 'january' | 'february' | 'march' | 'april' | 'may' | 'june'
  | 'july' | 'august' | 'september' | 'october' | 'november' | 'december';
export const MONTHS: Month[] = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];
/** Real-world day counts. Non-leap; the sim doesn't model leap years. */
export const MONTH_DAYS: Record<Month, number> = {
  january:   31, february:  28, march:     31, april:     30,
  may:       31, june:      30, july:      31, august:    31,
  september: 30, october:   31, november:  30, december:  31,
};
/** Which season each month falls in. Northern-hemisphere split. Used by
 *  the calendar derivation in Sim.getDate(), and (in the future) by
 *  crop-seasonality logic to decide whether a given seed can be planted
 *  this month, will die when winter rolls in, etc. */
export const MONTH_SEASON: Record<Month, Season> = {
  december: 'winter', january: 'winter',  february: 'winter',
  march:    'spring', april:   'spring',  may:      'spring',
  june:     'summer', july:    'summer',  august:   'summer',
  september:'fall',   october: 'fall',    november: 'fall',
};
/** Cumulative day-of-year (1-based) for the FIRST of each month. So
 *  `MONTH_FIRST_DOY.march === 60` means March 1 is day 60 of the year.
 *  Computed once at module load so getDate() is O(1) instead of looping. */
export const MONTH_FIRST_DOY: Record<Month, number> = (() => {
  const out = {} as Record<Month, number>;
  let acc = 1;
  for (const m of MONTHS) { out[m] = acc; acc += MONTH_DAYS[m]; }
  return out;
})();
export const DAYS_PER_YEAR = 365;

export type Weekday =
  | 'monday' | 'tuesday' | 'wednesday' | 'thursday'
  | 'friday' | 'saturday' | 'sunday';
export const WEEKDAYS: Weekday[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];
export const DAYS_PER_WEEK = 7;

/** Where Day 1 of the game lands in the calendar. March 1 = first day
 *  of Spring. With MONTH_FIRST_DOY.march = 60 this is day-of-year 60.
 *  Picking a Spring start so a fresh save feels immediately playable
 *  (winter without the greenhouse mechanic would just lock the player out). */
export const GAME_START_DOY = 60;             // March 1

/** Forward-looking: a crop's seasonality rules. Wired into Sim via a
 *  `cropCalendar()` helper later, when we add per-crop month gating,
 *  winter-die behaviour, and greenhouse override. Defined here now so
 *  every system has the type-shape ready when the gameplay layer
 *  catches up. None of these fields are READ yet — adding them to a
 *  CropDef is the next milestone. */
export interface CropSeasonality {
  /** Months this crop can be PLANTED. Empty = always. */
  plantMonths?: Month[];
  /** Months this crop will grow without intervention. Outside this
   *  set, the crop pauses (and dies if `killedBy` matches). */
  growMonths?: Month[];
  /** Seasons that outright kill this crop unless it's in a greenhouse. */
  killedBy?: Season[];
  /** If true, the crop can be planted year-round inside a greenhouse. */
  greenhouseOverride?: boolean;
}

/** Fully-derived calendar position for a given game day. Stable across
 *  ticks within the same day — UI re-renders only when these fields
 *  change. `dayOfWeek` is 0-indexed (0=Mon, 6=Sun). */
export interface GameDate {
  /** 1-based absolute day count since save start. */
  dayAbsolute: number;
  /** 1-based year. */
  year: number;
  month: Month;
  /** 0-based month index, useful for math. */
  monthIdx: number;
  /** 1..28/30/31 — day within the current month. */
  dayOfMonth: number;
  /** 1..365 — day within the current year. */
  dayOfYear: number;
  /** 0..6 — day within the current week (0=Mon, 6=Sun). */
  dayOfWeek: number;
  weekday: Weekday;
  /** 1..53 — ISO-ish week of the year (Jan 1 = week 1). */
  weekOfYear: number;
  season: Season;
}

/** Deprecated. Kept exported so any old code referencing it still
 *  type-checks; the value (7) now coincidentally matches DAYS_PER_WEEK.
 *  Use MONTHS / MONTH_DAYS for season math instead. */
export const SEASON_DAYS = 7;
export const GRANARY_CAP = 100;
export const GRANARY_COVERAGE_RADIUS = 4;

export const MILL_DURATION = 8;
/** Legacy single-batch wheat recipe — kept for any code path that still
 *  reads the flat constants. New queued batches use the per-kind splits below. */
export const MILL_INPUT = 5;
export const MILL_OUTPUT = 10;
/** Per-batch grain input/output for the queued mill. One batch grinds
 *  MILL_INPUT_X grain into MILL_OUTPUT_X flour over MILL_DURATION seconds. */
export const MILL_INPUT_WHEAT = 5;
export const MILL_INPUT_CORN = 5;
export const MILL_OUTPUT_WHEAT = 1;
export const MILL_OUTPUT_CORN = 1;

// ── Truck schedule ──
export const TRUCK_ARRIVAL_INTERVAL = 45;    // seconds between arrivals
export const TRUCK_LOAD_WINDOW = 6;          // seconds the truck waits to be loaded
export const TRUCK_TRAVEL_TIME = 8;          // seconds to city
/** Max items the truck bed can hold per trip. Forces the player to manage
 *  multiple runs instead of bottoming out the entire harvest in one go. */
export const TRUCK_CARGO_CAP = 200;
/** Max items the depot platform can stack waiting for pickup. */
export const TRUCK_PLATFORM_CAP = 200;
// total round-trip when departed = TRUCK_TRAVEL_TIME (to city) + TRUCK_TRAVEL_TIME (back)

// ── Mailbox ──
export const MAILBOX_DELIVERY_TIME = 30;   // seconds for a seed order to arrive

// ── XP ─────────────────────────────────────────────────────────────
export const XP_REWARDS = {
  placeField: 1,
  plant: 1,
  harvest: 2,
  startMill: 4,
  sellWheat: 1,
  sellCorn: 2,
  sellCarrot: 1,
  sellFlour: 2,
  sellWood: 1,
  buyBuilding: 5,
  chopTree: 3,
  clearStump: 1,
  clearBush: 1,
  clearSapling: 1,
  breakBoulder: 2,
  water: 1,
  forageMushroom: 2,
  forageBerry: 2,
  plantBerry: 1,
};

export const XP_FOR_LEVEL = [0, 0, 12, 35, 80, 160, 280, 460, 700, 1000];

export const UNLOCKS: { level: number; key: string; label: string }[] = [
  { level: 1, key: 'hands',   label: 'Hands & Inspect' },
  { level: 1, key: 'field',   label: 'Hoe (till grass → field)' },
  { level: 1, key: 'plant',   label: 'Plant Seeds' },
  { level: 1, key: 'axe',     label: 'Axe (chop trees → wood)' },
  { level: 1, key: 'pickaxe', label: 'Pickaxe (break boulders → stone)' },
  { level: 1, key: 'water',   label: 'Watering Can (faster growth)' },
  { level: 1, key: 'scythe',  label: 'Sickle (harvest crops + bulk reap)' },
  { level: 2, key: 'corn',    label: 'Corn Seeds' },
  { level: 2, key: 'carrot',  label: 'Carrot Seeds' },
  { level: 2, key: 'seedBag', label: 'Seed Bag (bulk plant)' },
];
